import { createContext, useContext } from 'react';

/** What a FormField tells the control inside it, so the label and messages describe it. */
export interface FormFieldContextValue {
    id: string;
    describedBy: string | undefined;
    invalid: boolean;
}

export const FormFieldContext = createContext<FormFieldContextValue | null>(null);

/** Joins the ids that are present into one `aria-describedby` value. */
export function joinIds(...ids: (string | undefined)[]): string | undefined {
    const present = ids.filter((id): id is string => id !== undefined && id !== '');
    return present.length > 0 ? present.join(' ') : undefined;
}

interface ControlOwnProps {
    id?: string;
    invalid?: boolean;
    'aria-describedby'?: string;
}

interface ControlAccessibleProps {
    id: string | undefined;
    'aria-invalid': true | undefined;
    'aria-describedby': string | undefined;
}

/**
 * The id and ARIA wiring for a control. A control's own props win; inside a FormField the
 * gaps are filled from it, so the field's label points at the control and its hint and
 * error describe it.
 */
export function useFormFieldControl(props: ControlOwnProps): ControlAccessibleProps {
    const field = useContext(FormFieldContext);
    const invalid = props.invalid ?? field?.invalid ?? false;

    return {
        id: props.id ?? field?.id,
        'aria-invalid': invalid ? true : undefined,
        'aria-describedby': joinIds(props['aria-describedby'], field?.describedBy),
    };
}
